<template>
  <div class="view" v-if="canEdit || canDelete" v-cloak>
    <GridPanelHeader :title="$t('common.parcels')" :grid-ref="$refs.parcelGrid" :show-add="true" :show-filter="false" :show-refresh="true" @add="handleAddClick">
      <template #actions>
        <button
          v-if="isSuperAdmin"
          type="button"
          class="group-actions-button"
          :title="$t('works.parcels.move_all_parcel_to_next_year') + ' (' + totalCount + ')'"
          @click="onMoveAllParcelToNextYear"
        >
          {{ $t("works.parcels.move_all_parcel_to_next_year_short") + " (" + totalCount + ")" }}
        </button>
        <button
          v-if="isSuperAdmin && parcelSyncSessionsAvailable"
          type="button"
          class="group-actions-button"
          :title="$t('works.parcels.sync_sessions_open')"
          @click="openParcelSyncSessions"
        >
          <i class="fa fa-history" aria-hidden="true"></i>
          {{ $t("works.parcels.sync_sessions_open") }}
        </button>
      </template>
    </GridPanelHeader>
    <DxDataGrid
      ref="parcelGrid"
      class="parcel_grid"
      :data-source="parcelsDataSurce"
      :show-borders="true"
      :column-auto-width="true"
      :remote-operations="true"
      :hover-state-enabled="true"
      :show-column-headers="true"
      :repaint-changes-only="true"
      :show-column-lines="true"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :selected-row-keys="selectedParcelUuids"
      @selection-changed="onGridSelectionChanged($event)"
      @row-click="onRowClick"
      @exporting="onExporting"
      @editing-start="onEditingStart"
      @row-prepared="onRowPrepared"
      @cell-prepared="onCellPrepared"
      @option-changed="onOptionChanged"
      :element-attr="gridAttributes"
      :paging="{ pageSize: 25 }"
    >
      <DxColumn
        dataField="name"
        :caption="$t('common.name')"
        :filter-operations="['contains']"
        :allowSorting="false"
        width="12%"
        :min-width="200"
      />
      <DxColumn
        dataField="surface"
        :caption="$t('common.surface_ha')"
        :filter-operations="['contains']"
        :allowSorting="false"
        dataType="number"
        alignment="center"
        width="12%"
        :min-width="100"
      />
      <DxColumn
        dataField="layer"
        :caption="$t('common.layer')"
        :filter-operations="['contains']"
        :allowSorting="false"
        :allowFiltering="true"
        alignment="center"
        width="8%"
        :allowHeaderFiltering="false"
        :allowEditing="false"
        cell-template="layerCellTemplate"
        :min-width="100"
      />
      <template #layerCellTemplate="{ data }">
        <LayerSvg :layer="data.data?.layer?.coordinates[0] ?? []" />
      </template>

      <DxColumn
        dataField="is_manual"
        cell-template="isManualImportViewTemplate"
        :caption="$t('works.parcels.data_input')"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        :allowSorting="false"
        :allowFiltering="true"
        :allowEditing="false"
        alignment="center"
        :min-width="100"
        :calculateCellValue="getManualImportText"
      >
        <DxLookup :data-source="manual_import_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #isManualImportViewTemplate="{ data }">
        <span>
          {{ getManualImportText(data.data.is_manual) }}
        </span>
      </template>

      <DxColumn
        dataField="department_uuid"
        cell-template="departmentViewTemplate"
        :caption="$t('common.unit')"
        :filter-operations="['=']"
        :allowSorting="false"
        alignment="center"
        :min-width="150"
        width="16%"
        :calculateCellValue="getDepartmentColumnText"
      >
        <DxLookup :data-source="departments" value-expr="uuid" display-expr="name" />
      </DxColumn>
      <template #departmentViewTemplate="{ data }">
        <span>
          {{ getDepartmentColumnText(data.data.department_uuid) }}
        </span>
      </template>
      <DxColumn
        dataField="settlement"
        :caption="$t('common.settlement')"
        :filter-operations="['contains']"
        :allowSorting="false"
        width="12%"
        :min-width="100"
      />
      <DxColumn
        dataField="season_uuid"
        cell-template="seasonViewTemplate"
        :caption="$t('common.season')"
        :filter-operations="['=']"
        :allowSorting="false"
        alignment="center"
        width="15%"
        :min-width="150"
        :calculateCellValue="getSeasonColumnText"
      >
        <DxLookup :data-source="seasons" value-expr="season_uuid" display-expr="name" />
      </DxColumn>
      <template #seasonViewTemplate="{ data }">
        <span>
          {{ getSeasonColumnText(data.data.season_uuid) }}
        </span>
      </template>
      <DxColumn
        dataField="culture_uuid"
        cell-template="cultureViewTemplate"
        :caption="$t('common.culture')"
        :filter-operations="['=']"
        :allowSorting="false"
        alignment="center"
        width="11%"
        :min-width="130"
        :calculateCellValue="getCultureColumnText"
      >
        <DxLookup :data-source="cultures" value-expr="culture_uuid" display-expr="name" />
      </DxColumn>
      <template #cultureViewTemplate="{ data }">
        <span>
          {{ getCultureColumnText(data.data.culture_uuid) }}
        </span>
      </template>
      <DxColumn
        dataField="variety"
        :caption="$t('common.variety')"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
        width="11%"
        :min-width="110"
      />
      <DxColumn
        dataField="remote_id"
        :caption="$t('common.remote_id')"
        :filter-operations="['contains']"
        :allowSorting="false"
        :min-width="100"
      />
      <DxColumn
        dataField="is_group"
        cell-template="isGroupViewTemplate"
        :caption="$t('works.parcels.is_group')"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        :allowSorting="false"
        :allowFiltering="true"
        :allowEditing="false"
        alignment="center"
        :min-width="100"
        :calculateCellValue="getYesNoColumnText"
      >
        <DxLookup :data-source="yes_no_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #isGroupViewTemplate="{ data }">
        <span>
          {{ getYesNoColumnText(data.data.is_group) }}
        </span>
      </template>

      <DxColumn
        dataField="parcel_group_name"
        :caption="$t('works.parcels.group_name')"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
        :min-width="130"
      />

      <DxColumn
        dataField="updated_at"
        :caption="$t('common.last_update')"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
        width="11%"
        :min-width="150"
        data-type="date"
        :format="getDxGridDefaultDateFormat + ' HH:mm'"
      />

      <DxColumn
        dataField="is_active"
        :caption="$t('common.active')"
        data-type="number"
        :allow-sorting="false"
        :allow-filtering="true"
        :filter-operations="['=']"
        alignment="center"
        :min-width="140"
        cell-template="isActiveViewTemplate"
        :calculateCellValue="(row) => getActiveStatusName(row.is_active)"
      >
        <DxLookup :data-source="active_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #isActiveViewTemplate="{ data }">
        <span>{{ getActiveStatusName(data.data.is_active) }}</span>
      </template>

      <DxColumn
        :width="60"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="dynamicMenuCell"
      />
      <template #dynamicMenuCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs.parcelGrid"
          row-key="uuid"
          :row="data.data"
          :items="getParcelActionItems(data.data)"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="allowDeleting"
        />
      </template>

      <DxSelection mode="multiple" showCheckBoxesMode="always" selectAllMode="page" />
      <DxFilterRow :visible="true" />
      <DxGrouping :autoExpandAll="true" />
      <DxGroupPanel :visible="false" />
      <DxColumn type="buttons" :visible="false" :show-in-column-chooser="false" />
      <DxEditing mode="row" :allowUpdating="allowUpdating" :allowAdding="false" :allowDeleting="allowDeleting" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('common.parcels'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'Parcels'" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxSummary>
        <!-- Total summary for the whole grid -->
        <DxTotalItem column="surface" summaryType="sum" valueFormat="#,##0.##" displayFormat="Total: {0}" />
      </DxSummary>
    </DxDataGrid>
    <ParcelSyncHistoryPopup
      :visible="showParcelSyncHistory"
      :parcel-name="selectedParcelName"
      :loading="parcelSyncHistoryLoading"
      :logs="parcelSyncHistory"
      @close="showParcelSyncHistory = false"
    />
    <ParcelSyncSessionsPopup
      :visible="showParcelSyncSessions"
      :loading="parcelSyncSessionsLoading"
      :sessions="parcelSyncSessions"
      @close="showParcelSyncSessions = false"
    />
    <GroupEdit
      ref="groupEdit"
      v-if="showGroupEditDialog"
      :visible="showGroupEditDialog"
      :selectedParcelUuids="allSelectedParcelUuids"
      :groupAction="groupAction"
      :season_uuid="getLastSeasonUuidFilterValue()"
      :last_payload="last_payload"
      :totalCount="totalCount"
      @close="showGroupEditDialog = false"
      @continue="onBulkActionApplied"
    />
    <GridGroupActions
      ref="groupActions"
      :grid-ref="$refs.parcelGrid"
      :selected-count="allSelectedParcelUuids.length"
      :show-delete-action="canDelete"
      delete-endpoint="/parcels/bulk"
      :delete-rows-by-uuid="selectedParcelRowsByUuid"
      :allow-deleting="allowDeleting"
      :delete-uuids="allSelectedParcelUuids"
      @cleared="onSelectionCleared"
      @delete-success="onBulkDeleteSuccess"
    >
      <button type="button" class="group-actions-button" @click="onParcelCultureChange">
        {{ $t("bulk_actions.parcel_culture_change") }}
      </button>
      <button type="button" class="group-actions-button" @click="onParcelGroupChange">
        {{ $t("bulk_actions.parcel_group_change") }}
      </button>
      <DxDropDownButton
        v-if="canEdit"
        :text="$t('bulk_actions.set_status')"
        :items="statusActionItems"
        display-expr="label"
        key-expr="id"
        styling-mode="outlined"
        :drop-down-options="{ width: 180 }"
        @item-click="onStatusActionClick"
      />
      <div style="width: 100px"></div>
    </GridGroupActions>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { canDelete, canEdit, permissions, permissionTypes } from "@/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import {
  DxDataGrid,
  DxColumn,
  DxSelection,
  DxFilterRow,
  DxGrouping,
  DxGroupPanel,
  DxPaging,
  DxLookup,
  DxPager,
  DxEditing,
  DxTexts,
  DxToolbar,
  DxItem,
  DxExport,
  DxColumnChooser,
  DxColumnFixing,
  DxSummary,
  DxTotalItem,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import LayerSvg from "@/components/Map/LayerSvg.vue";
import { errorMessage, successMessage } from "@/helpers/common";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";
import GroupEdit from "./partials/GroupEdit.vue";
import { PARCEL_CULTURE_CHANGE, PARCEL_GROUP_CHANGE, PARCEL_COPY } from "@/helpers/bulkActionItems";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridGroupActions from "@/components/DxGrid/GridGroupActions.vue";
import ParcelSyncHistoryPopup from "./partials/ParcelSyncHistoryPopup.vue";
import ParcelSyncSessionsPopup from "./partials/ParcelSyncSessionsPopup.vue";
import axios from "@/plugins/axios";

export default {
  name: "Parcels",
  middleware: ["auth", "check-company-admin"],
  data() {
    return {
      parcelsDataSurce: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          let pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
            calculate_total_surface: true,
          };

          let season_found = false;
          let i = 0;
          // Check if the filter contains 'season_uuid'
          if (payload.filter && Array.isArray(payload.filter)) {
            // Find all indexes where the filter is for 'season_uuid'
            while (i < payload.filter.length) {
              if (payload.filter[i] && payload.filter[i] == "season_uuid") {
                season_found = true;
                const season_uuid = payload.filter[i + 2];
                // Remove this and the next two keys (total 3)
                payload.filter.splice(i, 3);
                payload.filter.push(["season_uuid", "=", season_uuid]);
                break; // Exit the loop if we found a season filter
              } else {
                i++;
              }
            }
          }

          if (!season_found) {
            if (payload.filter && Array.isArray(payload.filter)) {
              // Find all indexes where the filter is for 'season_uuid'
              i = 0;
              while (i < payload.filter.length) {
                if (Array.isArray(payload.filter[i]) && payload.filter[i][0] == "season_uuid") {
                  season_found = true;
                  break; // Exit the loop if we found a season filter
                } else {
                  i++;
                }
              }
            }
          }

          if (!season_found) {
            console.error("No season filter found in the payload (parcels).");
            return dataSource; // If no season filter, return empty data source
          }

          try {
            this.loaded_season_uuid = this.seasons.find(
              (s) => s.season_uuid == payload.filter.find((f) => f[0] === "season_uuid")?.[2],
            )?.season_uuid;

            await this.getParcels(payload).then((response) => {
              this.last_payload = payload; // Store the last payload for reference
              this.totalCount = response?.pagination?.total ?? 0; // Update the loaded total count
              dataSource.data = response.data;
              dataSource.totalCount = response?.pagination?.total ?? 1;
              dataSource.summary = [response?.summary?.total_surface ?? 0];
              this.syncSelectedParcelRowsByUuid(response.data);

              // Get list of uuid from response.data
              this.grid_data_uuids = Array.isArray(response.data) ? response.data.map((item) => item.uuid) : [];
              this.restoreSelection();
            });

            return dataSource;
          } catch (error) {
            console.error("Error fetching parcels:", error);
            throw error;
          }
        },
        remove: this.removeParcel,
      }),
      groupAction: null,
      showGroupEditDialog: false,
      selectedParcelUuids: [],
      allSelectedParcelUuids: [], // <-- Track all selected items here
      pageSizes: [15, 50, 100],
      seasons: [],
      cultures: [],
      loaded_season_uuid: null,
      last_payload: null,
      totalCount: 0,
      yes_no_list: [
        {
          id: 1,
          label: this.$t("common.yes"),
        },
        {
          id: 0,
          label: this.$t("common.no"),
        },
      ],
      manual_import_list: [
        {
          id: 1,
          label: this.$t("common.manual"),
        },
        {
          id: 0,
          label: this.$t("common.import"),
        },
      ],
      active_list: [
        { id: 1, label: this.$t("common.active") },
        { id: 0, label: this.$t("common.inactive") },
      ],
      grid_data_uuids: [],
      selectedParcelRowsByUuid: {},
      parcelSyncHistoryEnabled: false,
      showParcelSyncHistory: false,
      parcelSyncHistoryLoading: false,
      parcelSyncHistory: [],
      selectedParcelName: "",
      parcelSyncSessionsAvailable: false,
      showParcelSyncSessions: false,
      parcelSyncSessionsLoading: false,
      parcelSyncSessions: [],
    };
  },
  components: {
    Forbidden,
    DxDataGrid,
    DxColumn,
    DxSelection,
    DxFilterRow,
    DxGrouping,
    DxGroupPanel,
    DxPaging,
    LayerSvg,
    DxLookup,
    DxPager,
    DxEditing,
    DxTexts,
    DxToolbar,
    DxItem,
    DxExport,
    DxColumnChooser,
    DxColumnFixing,
    GroupEdit,
    DxSummary,
    DxTotalItem,
    DxActionMenu,
    GridPanelHeader,
    GridGroupActions,
    DxDropDownButton,
    ParcelSyncHistoryPopup,
    ParcelSyncSessionsPopup,
  },
  computed: {
    gridAttributes() {
      return {
        class: "smagria-grid",
      };
    },
    ...mapGetters({
      user: "auth/user",
      departments: "company/departments",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.PARCELS,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    isSuperAdmin() {
      return Boolean(this.user && this.user.is_superadmin === 1);
    },
    statusActionItems() {
      return [
        { id: 1, label: this.$t("bulk_actions.set_active") },
        { id: 0, label: this.$t("bulk_actions.set_inactive") },
      ];
    },
  },
  methods: {
    ...mapActions({
      deleteParcel: "works-parcels/deleteParcel", //check to delete if has stock
      getSeasons: "seasons/getSeasons",
      getParcels: "works-parcels/getParcels",
      getCultures: "works-cultures/getCultures",
      getDepartments: "company/getDepartments",
    }),
    syncSelectedParcelRowsByUuid(items = []) {
      const selectedUuidSet = new Set(this.allSelectedParcelUuids);
      const nextSelectedParcelRowsByUuid = {};

      Object.keys(this.selectedParcelRowsByUuid).forEach((uuid) => {
        if (selectedUuidSet.has(uuid)) {
          nextSelectedParcelRowsByUuid[uuid] = this.selectedParcelRowsByUuid[uuid];
        }
      });

      (Array.isArray(items) ? items : []).forEach((item) => {
        if (item && selectedUuidSet.has(item.uuid)) {
          nextSelectedParcelRowsByUuid[item.uuid] = item;
        }
      });

      this.selectedParcelRowsByUuid = nextSelectedParcelRowsByUuid;
    },
    allowUpdating(component) {
      // Allow editing if the row is not deleted and has no stock movement
      return this.canEdit && !component.row.data.deleted_at;
    },
    allowDeleting(component) {
      const rowData = component && component.row ? component.row.data : component;
      if (!rowData) return false;
      // Hide delete icon if deleted_at is set

      return this.canDelete && rowData.is_deletable && !rowData.deleted_at && !rowData.is_group;
    },
    getYesNoColumnText(value) {
      if (value === null) {
        return "";
      }
      return this.yes_no_list.find((item) => item.id == value)?.label;
    },
    getManualImportText(value) {
      if (value === null) {
        return "";
      }
      return this.manual_import_list.find((item) => item.id == value)?.label;
    },
    getActiveStatusName(value) {
      return this.active_list.find((item) => item.id == value)?.label ?? "";
    },
    getCultureColumnText(culture_uuid) {
      const culture = this.cultures.find((c) => c.culture_uuid === culture_uuid);
      return culture ? culture.name : "";
    },
    getSeasonColumnText(season_uuid) {
      const season = this.seasons.find((s) => s.season_uuid === season_uuid);
      return season ? season.name : "";
    },
    getDepartmentColumnText(department_uuid) {
      const department = this.departments.find((d) => d.uuid == department_uuid);
      return department ? department.name : "";
    },
    handleAddClick(e) {
      this.$router.push({
        name: "parcels-details",
        params: {
          uuid: "add",
        },
      });
    },
    onCellPrepared(e) {
      // Check if this is a selection checkbox cell in a data row
      if (
        e.rowType === "data" &&
        e.column.command === "select" &&
        e.data.is_group === 1 // or === true, depending on your data
      ) {
        // Hide the checkbox
        e.cellElement.innerHTML = "";
      }
    },
    getParcelActionItems(row) {
      const items = [
        {
          key: "view_on_map",
          label: this.$t("works.parcels.view_on_map"),
          text: this.$t("works.parcels.view_on_map"),
          icon: require("@/assets/images/icons/pin.svg"),
          action: () => this.openParcelOnMap(row),
        },
      ];

      if (this.parcelSyncHistoryEnabled && this.isSuperAdmin) {
        items.push({
          key: "sync_history",
          label: this.$t("works.parcels.sync_history_open"),
          text: this.$t("works.parcels.sync_history_open"),
          iconClass: "fa fa-history",
          action: () => this.openParcelSyncHistory(row),
        });
      }

      return items;
    },
    async loadParcelSyncHistorySetting() {
      try {
        const response = await axios.get("/parcels/sync-history/status");
        this.parcelSyncHistoryEnabled = response.data.enabled === true;
      } catch (error) {
        this.parcelSyncHistoryEnabled = false;
      }
      if (!this.isSuperAdmin) return;
      try {
        const response = await axios.get("/import-configurator/parcel-data-sync/status");
        this.parcelSyncSessionsAvailable = response.data.enabled === true;
      } catch (error) {
        this.parcelSyncSessionsAvailable = false;
      }
    },
    async openParcelSyncSessions() {
      if (!this.isSuperAdmin || !this.parcelSyncSessionsAvailable) return;
      this.parcelSyncSessions = [];
      this.showParcelSyncSessions = true;
      this.parcelSyncSessionsLoading = true;
      try {
        const response = await axios.get("/parcels/sync-history/sessions");
        this.parcelSyncSessions = Array.isArray(response.data.data) ? response.data.data : [];
      } catch (error) {
        errorMessage(this, this.$t("works.parcels.sync_sessions_load_failed"));
      } finally {
        this.parcelSyncSessionsLoading = false;
      }
    },
    async openParcelSyncHistory(parcel) {
      if (!parcel?.uuid || !this.parcelSyncHistoryEnabled) return;
      this.selectedParcelName = parcel.name || "";
      this.parcelSyncHistory = [];
      this.showParcelSyncHistory = true;
      this.parcelSyncHistoryLoading = true;
      try {
        const response = await axios.get(`/parcels/${parcel.uuid}/sync-history`);
        this.parcelSyncHistory = Array.isArray(response.data.data) ? response.data.data : [];
      } catch (error) {
        errorMessage(this, this.$t("works.parcels.sync_history_load_failed"));
      } finally {
        this.parcelSyncHistoryLoading = false;
      }
    },
    hasParcelGeometry(row) {
      const coords = row?.layer?.coordinates;
      return Array.isArray(coords) && coords.length > 0 && Array.isArray(coords[0]) && coords[0].length > 0;
    },
    openParcelOnMap(row) {
      if (!row?.uuid) {
        return;
      }
      if (!this.hasParcelGeometry(row)) {
        errorMessage(this, this.$t("works.parcels.no_geometry"));
        return;
      }

      this.$router.push({
        path: "/works/applyArticles",
        query: {
          showMapp: "true",
          parcel: row.uuid,
          parcel_name: row.name,
          season_uuid: row.season_uuid,
        },
      });
    },
    onGridSelectionChanged(e) {
      // Update the selectedParcelUuids based on the grid's selected rows
      this.selectedParcelUuids = e.selectedRowKeys;
      // If no rows are selected, reset the selectedParcelUuids
      if (this.selectedParcelUuids.length === 0) {
        this.selectedParcelUuids = [];
      }

      const gridData = this.$refs.parcelGrid?.instance?.getDataSource()?.items?.() ?? [];

      // Filter out parcels where is_group = 1
      if (this.$refs.parcelGrid && this.$refs.parcelGrid.instance) {
        let group_found = false;
        e.currentSelectedRowKeys.forEach((key) => {
          const item = gridData.find((row) => row.uuid === key);
          if (item && item.is_group === 1) {
            this.selectedParcelUuids = this.selectedParcelUuids.filter((uuid) => uuid !== key);
            group_found = true;
          }
        });

        if (group_found) {
          this.$refs.parcelGrid.instance.selectRows(this.selectedParcelUuids, false);
          return;
        }
      }

      // Add selected keys that are in current grid data
      e.currentSelectedRowKeys.forEach((key) => {
        if (this.grid_data_uuids.includes(key) && !this.allSelectedParcelUuids.includes(key)) {
          this.allSelectedParcelUuids.push(key);
        }
      });

      // Remove deselected keys that are in current grid data
      e.currentDeselectedRowKeys.forEach((key) => {
        if (this.grid_data_uuids.includes(key)) {
          const idx = this.allSelectedParcelUuids.indexOf(key);
          if (idx !== -1) {
            this.allSelectedParcelUuids.splice(idx, 1);
          }
        }
      });

      this.syncSelectedParcelRowsByUuid(gridData);
    },
    onRowClick(e) {
      const rowKey = e.key; // Get the unique key of the clicked row
      const gridData = this.$refs.parcelGrid?.instance?.getDataSource()?.items?.() ?? [];

      // Filter out parcels where is_group = 1
      if (this.$refs.parcelGrid && this.$refs.parcelGrid.instance) {
        const item = gridData.find((row) => row.uuid === rowKey);
        if (item && item.is_group === 1) {
          this.selectedParcelUuids = this.selectedParcelUuids.filter((uuid) => uuid !== rowKey);
          this.$refs.parcelGrid.instance.selectRows(this.selectedParcelUuids, false);
          return;
        }
      }

      const selectedIndex = this.selectedParcelUuids.indexOf(rowKey);
      if (selectedIndex === -1) {
        // If the row is not selected, add it to the selection
        this.selectedParcelUuids.push(rowKey);
      } else {
        // If the row is already selected, remove it from the selection
        this.selectedParcelUuids.splice(selectedIndex, 1);
      }

      this.refreshAllSelectedRowsData(rowKey);
      this.syncSelectedParcelRowsByUuid(gridData);

      // Update the grid's selected rows
      this.$refs.parcelGrid.instance.selectRows(this.selectedParcelUuids, false);
    },
    refreshAllSelectedRowsData(rowKey) {
      const selectedIndex = this.allSelectedParcelUuids.indexOf(rowKey);
      if (selectedIndex === -1) {
        // If the row is not selected, add it to the selection
        this.allSelectedParcelUuids.push(rowKey);
      } else {
        // If the row is already selected, remove it from the selection
        this.allSelectedParcelUuids.splice(selectedIndex, 1);
      }
    },
    onExporting(e) {
      e.fileName = "Parcels_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");

      if (e.format === "xlsx") {
        const fields = ["deleted_at", "is_group"];
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Parcels");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          selectedRowsOnly: e.selectedRowsOnly, // <-- This is set automatically
          customizeCell: ({ gridCell, excelCell }) => {
            if (gridCell?.data && gridCell?.column?.dataField in gridCell.data) {
              if (gridCell?.column?.dataField == "layer" && excelCell.row > 1) {
                excelCell.value = this.getYesNoColumnText(!!gridCell.data[gridCell?.column?.dataField]);
              } else if (gridCell?.column?.dataField == "department_uuid" && excelCell.row > 1) {
                excelCell.value = this.getDepartmentColumnText(gridCell.data[gridCell?.column?.dataField]);
              } else if (gridCell?.column?.dataField == "season_uuid" && excelCell.row > 1) {
                excelCell.value = this.getSeasonColumnText(gridCell.data[gridCell?.column?.dataField]);
              } else if (gridCell?.column?.dataField == "culture_uuid" && excelCell.row > 1) {
                excelCell.value = this.getCultureColumnText(gridCell.data[gridCell?.column?.dataField]);
              } else if (gridCell?.column?.dataField == "is_manual" && excelCell.row > 1) {
                excelCell.value = this.getManualImportText(!!gridCell.data[gridCell?.column?.dataField]);
              } else if (gridCell?.column?.dataField === "is_active" && excelCell.row > 1) {
                excelCell.value = this.getActiveStatusName(gridCell?.data?.is_active);
              } else if (fields.includes(gridCell?.column?.dataField) && excelCell.row > 1) {
                excelCell.value = this.getYesNoColumnText(!!gridCell.data[gridCell?.column?.dataField]);
              } else if (gridCell?.column?.dataField == "updated_at" && excelCell.row > 1) {
                excelCell.value = gridCell.data[gridCell?.column?.dataField];
              } else if (gridCell?.column?.dataField == "surface" && excelCell.row > 1) {
                excelCell.value = parseFloat(gridCell.data[gridCell?.column?.dataField]) ?? 0;
              }
            }

            excelCell.value = excelCell.value == null ? "" : excelCell.value;
            excelCell.numFmt = null; // Reset number format to default else style exception in excel
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    onRowPrepared(e) {
      if (e.rowType === "data") {
        if (e.data.deleted_at) {
          e.rowElement.style.color = "red";
          e.rowElement.style.fontStyle = "italic";
        }
      }
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        const instance = this.$refs?.parcelGrid?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
        }
      }
    },
    onEditingStart(e) {
      e.cancel = true; // Cancel editing for this row
      this.editingRowData = e.data; // Store the row data for editing

      if (e.data.has_stock_movement) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_edit_with_stock_movement"));
        return;
      }

      if (e.data.deleted_at) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_edit_deleted"));
        return;
      }

      this.$router.push({
        name: "parcels-details",
        params: {
          uuid: e.data.uuid,
        },
      });
    },
    async removeParcel(key) {
      if (!key) {
        console.error("Invalid key provided for deletion:", key);
        return;
      }

      let vm = this;
      try {
        await this.deleteParcel({ uuid: key }).then((response) => {
          successMessage(vm, response.message);
        });
      } catch (error) {
        console.error("Error deleting scale :", error);
        throw error;
      }
    },
    onParcelCultureChange() {
      if (!this.allSelectedParcelUuids.length) {
        errorMessage(this, this.$t("bulk_actions.no_parcel_selected"));
        return;
      }
      this.groupAction = PARCEL_CULTURE_CHANGE; // Set the group action to culture change
      this.showGroupEditDialog = true; // Show the group edit dialog
    },
    onParcelGroupChange() {
      if (!this.allSelectedParcelUuids.length) {
        errorMessage(this, this.$t("bulk_actions.no_parcel_selected"));
        return;
      }
      this.groupAction = PARCEL_GROUP_CHANGE; // Set the group action to group change
      this.showGroupEditDialog = true; // Show the group edit dialog
    },
    onMoveAllParcelToNextYear() {
      if (this.user && this.user.is_superadmin === 1) {
        if ((this.totalCount ?? 0) <= 0) {
          errorMessage(this, this.$t("bulk_actions.no_parcel_selected"));
          return;
        }

        this.groupAction = PARCEL_COPY; //
        this.showGroupEditDialog = true; // Show the group edit dialog
      }
    },
    onStatusActionClick(e) {
      this.onSetActiveStatus(e?.itemData?.id === 1);
    },
    onSetActiveStatus(isActive) {
      if (!this.allSelectedParcelUuids.length) {
        errorMessage(this, this.$t("bulk_actions.no_parcel_selected"));
        return;
      }
      this.$refs.groupActions
        .runBulkAction({
          endpoint: "/parcels/bulk",
          action: "update",
          uuids: this.allSelectedParcelUuids,
          data: { is_active: isActive ? 1 : 0 },
          confirmTitle: this.$t("bulk_actions.confirm_title"),
          confirmMessage: this.$t("bulk_actions.confirm_update", { count: this.allSelectedParcelUuids.length }),
          emptyMessage: this.$t("bulk_actions.no_selection"),
          successMessageBuilder: (resp) =>
            this.$t("bulk_actions.updated_summary", {
              updated: resp?.data?.updated ?? resp?.updated ?? 0,
            }),
        })
        .then((result) => {
          if (result && result.cancelled) {
            return;
          }
          this.$refs.groupActions.clearSelection();
          this.reloadGrid();
        })
        .catch(() => {});
    },
    onBulkActionApplied() {
      this.reloadGrid();
    },
    onBulkDeleteSuccess() {
      this.reloadGrid();
    },
    reloadGrid() {
      if (this.$refs.parcelGrid && this.$refs.parcelGrid.instance) {
        this.$refs.parcelGrid.instance.refresh();
      }
    },
    getLastSeasonUuidFilterValue() {
      return this.loaded_season_uuid;
    },
    restoreSelection() {
      setTimeout(() => {
        this.selectedParcelUuids = this.allSelectedParcelUuids.filter((uuid) => this.grid_data_uuids.includes(uuid));
        if (this.$refs.parcelGrid && this.$refs.parcelGrid.instance) {
          const gridData = this.$refs.parcelGrid.instance?.getDataSource()?.items?.() ?? [];
          this.syncSelectedParcelRowsByUuid(gridData);
          this.$refs.parcelGrid.instance.selectRows(this.selectedParcelUuids, false);
        }
      }, 300);
    },
    onSelectionCleared() {
      this.selectedParcelUuids = [];
      this.allSelectedParcelUuids = [];
      this.selectedParcelRowsByUuid = {};
    },
  },
  mounted() {
    this.loadParcelSyncHistorySetting();
    this.getSeasons({ limit: 1000 })
      .then((response) => {
        this.seasons = response.data.map((season) => ({
          season_uuid: season.uuid,
          name: season.name,
          year: season.year, // Assuming 'year' is a property in the season object
        }));

        // Find the biggest UUID (lexicographically)
        const maxYear = Math.max(...this.seasons.map((s) => s.year));
        this.loaded_season_uuid = this.seasons.find((s) => s.year == maxYear)?.season_uuid;

        this.$nextTick(() => {
          // If you want to filter the grid immediately after loading
          if (this.$refs.parcelGrid && this.$refs.parcelGrid.instance) {
            //this.$refs.parcelGrid.instance.filter([["season_uuid", "=", this.biggest_year_uuid]]);
            // Set the filter value for the column (shows in filter row UI)
            this.$refs.parcelGrid.instance.columnOption("season_uuid", "filterValue", this.loaded_season_uuid);
          }

          /*if (this.$refs.parcelGrid && this.$refs.parcelGrid.instance) {
            this.$refs.parcelGrid.instance.selectRows(this.allSelectedParcelUuids, false);
          }*/
        });
      })
      .catch((error) => {
        console.error("Error fetching seasons:", error);
      });

    this.getCultures({
      enablePagination: 0,
      limit: 9999,
    })
      .then((resp) => {
        this.cultures = resp.data.map((culture) => ({
          culture_uuid: culture.uuid,
          name: culture.name,
        }));
      })
      .catch((e) => {
        reject(e);
      });
  },
};
</script>

